import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { ModalTransfer } from './FormularTransfer'
import {
  EticheaStareTransfer,
  confirmaStergere,
  avertismentAnulare,
} from './eveniment/stareTransfer'

/** „Mostre (7)" când știm numele, altfel numărul gol — tot mai bine decât nimic. */
function gestiune(id: number, nume: string | null): string {
  return nume ? `${nume} (${id})` : `gestiunea ${id}`
}

/**
 * Alimentarea gestiunii de sampling.
 *
 * Până acum se putea doar scădea — bonul de consum, la fiecare predare. Marfa trebuia să ajungă
 * acolo pe altă cale, iar un stoc care iese fără să fi intrat vreodată produce diferențe de
 * inventar, nu evidență.
 *
 * Documentul iese `tc-out` — transfer cu confirmare. Am încercat întâi `t-out`, transferul
 * direct din interfața EA, dar prin API el scoate marfa din gestiunea sursă și ignoră destinația:
 * verificat pe 20.08.2026, documentul t324, marfa n-a ajuns niciodată în gestiunea 27.
 *
 * Deci marfa intră în gestiune abia după documentul pereche „Transfer confirmat-Primire", făcut
 * în EA. Am încercat să-l facem prin API și chiar s-a creat — dar cu prețul de vânzare pe post de
 * cost, cu lot nou în loc de lotul care a plecat (fără lot, fără termen de valabilitate), și fără
 * să închidă livrarea: confirmarea e o schimbare de stare, nu un document. Vezi `primire.ts`.
 *
 * Pasul ăsta se uită — de aceea scrie pe fiecare transfer trimis, nu doar în manual.
 */

interface Transfer {
  id: number
  referinta: string
  /** Activitatea de care tine. Gol la transferurile generale, pornite de aici. */
  campanieId: number | null
  campanie: { id: number; cod: string; titlu: string } | null
  locSursa: number
  locDestinatie: number
  locSursaNume: string | null
  locDestinatieNume: string | null
  status: string
  eaDocNo: string | null
  raspunsEa: string | null
  observatii: string | null
  createdAt: string
  /** Tipul cu care a plecat: `tc-out` sau `t-out`. Decide ce document de primire se trimite. */
  doctype: string
  /** Ecranul de primire din EA, gata de deschis. `null` cand EA nu e configurat. */
  linkPrimireEa: string | null
  tcInStatus: string | null
  tcInDocNo: string | null
  tcInRaspuns: string | null
  linii: { sku: string; denumire: string; cantitate: number }[]
}

function mesaj(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? 'A apărut o eroare'
}

function zi(iso: string): string {
  const d = new Date(iso)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
}

export function TabTransferuri({ canWrite, canSterge }: { canWrite: boolean; canSterge: boolean }) {
  const [deschis, setDeschis] = useState(false)
  const queryClient = useQueryClient()

  /**
   * Documentul pereche de primire, care confirmă recepția în gestiunea destinație.
   *
   * Nu știm încă dacă se poate: add-on-ul pe care îl avem creează documente de IEȘIRE, iar
   * modulul de intrare nu e printre licențele active. Se încearcă fiindcă răspunsul lor e
   * ieftin de obținut și definitiv — iar dacă e „nu", rămâne scris pe transfer.
   */
  /**
   * Șterge transferul din evidența CRM.
   *
   * Nu șterge nimic în ExpertAccounts și nici nu poate: add-on-urile noastre scriu documente,
   * nu le retrag. Cine șterge de aici trebuie să fi șters deja acolo — altfel rămân invers,
   * document în ERP fără urmă în CRM.
   */
  const sterge = useMutation({
    mutationFn: async (id: number) => api.delete(`/marketing/sampling/transferuri/${id}`),
    onSuccess: () => {
      toast.success('Transfer șters din evidență')
      void queryClient.invalidateQueries({ queryKey: ['sampling', 'transferuri'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  /**
   * ANULEAZĂ transferul: emite în ExpertAccounts transferul INVERS și marchează rândul.
   *
   * Alta decât ștergerea de mai sus, și altă faptă. Ștergerea uită rândul și lasă marfa mutată
   * în ERP; anularea o aduce înapoi, cu document. Alexandru, 30.09.2026: transferul pentru „Her
   * Moment" plecase în gestiunea Sampling, nu în a lui Dorian.
   */
  const anuleaza = useMutation({
    mutationFn: async (id: number) =>
      (
        await api.post<{ stornoRefno: string | null; eaDocNo: string | null }>(
          `/marketing/sampling/transferuri/${id}/anuleaza`,
          {},
          { timeout: 120_000 },
        )
      ).data,
    onSuccess: (d) => {
      toast.success(
        d.stornoRefno
          ? `Anulat. Marfa s-a întors prin ${d.stornoRefno}${d.eaDocNo ? ` (${d.eaDocNo})` : ''}`
          : 'Transfer marcat anulat',
      )
      void queryClient.invalidateQueries({ queryKey: ['sampling', 'transferuri'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  /**
   * Întreabă EA dacă livrarea a fost primită. Nu trimite niciun document — primirea se face
   * acolo, cu „Primește livrarea", fiindcă e o schimbare de stare pe livrare, nu un document nou.
   */
  /**
   * Trimite documentul de primire prin API.
   *
   * Solicitare: transferul să se confirme automat din CRM, nu dintr-o pagină deschisă în EA.
   *
   * Se arată câte linii au plecat cu costul citit din `tc-out`: e cifra care spune dacă
   * documentul iese echilibrat. Zero linii cu cost înseamnă că EA nu ne dă `pin` prin API, iar
   * atunci prețul lipsește din cerere anume — mai bine îl deduce EA decât să-l punem greșit.
   */
  const primeste = useMutation({
    mutationFn: async (id: number) =>
      (
        await api.post<{
          ok: boolean
          docNo: string | null
          cuCost: number
          linii: number
          stare: string
          raspuns: string
        }>(`/marketing/sampling/transferuri/${id}/primire`, {}, { timeout: 120_000 })
      ).data,
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(
          `Primit în EA${r.docNo ? ` · ${r.docNo}` : ''} — ${r.cuCost} din ${r.linii} linii cu cost citit`,
        )
      } else {
        // Mesajul intreg, nu o felie: cand EA raspunde „ok" fara numar, textul spune ce sa
        // verifici in ERP. Taiat la 200 de caractere, exact partea aia se pierdea.
        toast.error(r.raspuns, { duration: 20_000 })
      }
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const verifica = useMutation({
    mutationFn: async (id: number) =>
      (
        await api.post<{ confirmat: boolean; docNoPrimire: string | null; detaliu: string }>(
          `/marketing/sampling/transferuri/${id}/primire/verifica`,
          {},
          { timeout: 60_000 },
        )
      ).data,
    onSuccess: (r) => {
      if (r.confirmat) toast.success(r.detaliu)
      else toast(r.detaliu, { icon: '⏳' })
      void queryClient.invalidateQueries({ queryKey: ['sampling', 'transferuri'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const transferuri = useQuery({
    queryKey: ['sampling', 'transferuri'],
    queryFn: async () => (await api.get<Transfer[]>('/marketing/sampling/transferuri')).data,
  })

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-b4l-text-muted">
          Mută produse dintr-o gestiune în gestiunea de sampling, ca să existe stoc pentru
          evenimente. Documentul se trimite în ExpertAccounts ca transfer între gestiuni. După
          primul transfer, verifică în ExpertAccounts că marfa a intrat în gestiunea de sampling.
        </p>
        {canWrite && <Button onClick={() => setDeschis(true)}>Transfer nou</Button>}
      </div>

      <Card>
        {transferuri.isLoading ? (
          <p className="p-4 text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : !transferuri.data?.length ? (
          <p className="p-4 text-sm text-b4l-text-muted">
            Niciun transfer. Gestiunea de sampling se alimentează de aici.
          </p>
        ) : (
          <div className="divide-y divide-b4l-border">
            {transferuri.data.map((t) => (
              <div key={t.id} className="p-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-b4l-text">{t.referinta}</span>
                  <EticheaStareTransfer transfer={t as never} />
                  {/*
                    De ce activitate tine transferul. Ecranul asta a ramas cel general — de aici
                    se vede tot, inclusiv ce s-a pornit din evenimente — deci trebuie sa spuna
                    pentru cine a plecat marfa, nu doar ca a plecat.
                  */}
                  {t.campanie && (
                    <Link
                      to={`/marketing/evenimente/${t.campanie.id}`}
                      className="rounded-full bg-b4l-blue-light px-2 py-0.5 text-xs text-b4l-blue-dark hover:underline"
                      title={t.campanie.titlu}
                    >
                      {t.campanie.cod}
                    </Link>
                  )}
                  <span className="ml-auto text-xs text-b4l-text-muted">{zi(t.createdAt)}</span>
                  {/*
                    ANULAREA stă înaintea ștergerii, fiindcă ea e reparația corectă a unui transfer
                    plecat greșit. Ștergerea rămâne pentru altceva: curățenie în evidență, după ce
                    documentul a fost rezolvat în ERP.
                  */}
                  {canWrite && (t.status === 'trimis' || t.status === 'inregistrat') && (
                    <button
                      disabled={anuleaza.isPending}
                      onClick={() => {
                        if (window.confirm(avertismentAnulare(t as never))) anuleaza.mutate(t.id)
                      }}
                      className="text-xs text-b4l-amber-dark hover:underline disabled:opacity-40"
                      title="Emite transferul invers în EA și marchează rândul anulat"
                    >
                      {anuleaza.isPending ? 'Se anulează…' : 'Anulează'}
                    </button>
                  )}
                  {canSterge && (
                    <button
                      onClick={() => {
                        if (confirmaStergere(t as never)) sterge.mutate(t.id)
                      }}
                      className="text-xs text-b4l-red hover:underline"
                      title="Șterge din evidența CRM"
                    >
                      Șterge
                    </button>
                  )}
                </div>
                <div className="mt-1 text-xs text-b4l-text-muted">
                  {gestiune(t.locSursa, t.locSursaNume)} →{' '}
                  {gestiune(t.locDestinatie, t.locDestinatieNume)} ·{' '}
                  {t.linii.map((l) => `${l.denumire} ×${l.cantitate}`).join(', ')}
                  {t.observatii && ` · ${t.observatii}`}
                </div>
                {t.status === 'esuat' && t.raspunsEa && (
                  <p className="mt-1 text-xs text-b4l-red">{t.raspunsEa.slice(0, 300)}</p>
                )}

                {/* Confirmarea recepției: doar pe transferurile care chiar au plecat SI n-au fost anulate. */}
                {t.status === 'trimis' && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                    {t.tcInStatus === 'trimis' ? (
                      <span className="rounded-full bg-b4l-green-light px-2 py-0.5 text-b4l-green-dark">
                        Primită în EA{t.tcInDocNo ? ` · ${t.tcInDocNo}` : ''}
                      </span>
                    ) : (
                      <>
                        <span className="rounded-full bg-b4l-amber-light px-2 py-0.5 text-b4l-amber-dark">
                          Marfa a plecat, dar nu e primită în gestiune
                        </span>
                        {canWrite && (
                          <Button
                            onClick={() => primeste.mutate(t.id)}
                            disabled={primeste.isPending}
                            className="!px-2 !py-1 !text-xs"
                          >
                            {primeste.isPending
                              ? 'Se trimite…'
                              : `Primește (trimite ${t.doctype === 't-out' ? 't-in' : 'tc-in'})`}
                          </Button>
                        )}
                        {canWrite && (
                          <Button
                            variant="secondary"
                            onClick={() => verifica.mutate(t.id)}
                            disabled={verifica.isPending}
                            className="!px-2 !py-1 !text-xs"
                          >
                            {verifica.isPending ? 'Verific…' : 'Verifică în EA'}
                          </Button>
                        )}
                        {/*
                          Legătura către EA rămâne, dar ca a doua cale: dacă documentul nu poate
                          pleca prin API, omul are unde să-l facă de mână fără să caute meniul.
                        */}
                        {t.linkPrimireEa && (
                          <a
                            href={t.linkPrimireEa}
                            target="_blank"
                            rel="noreferrer"
                            className="text-b4l-text-muted underline"
                            title="Se deschide «Documente noi» în EA. De acolo: Transfer confirmat-Primire → Încarcă lista → Primește livrarea"
                          >
                            sau manual în EA (Documente noi)
                          </a>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {deschis && <ModalTransfer onClose={() => setDeschis(false)} />}
    </div>
  )
}
